export type DecorCategoryId = "vehicle" | "person" | "animal" | "bench" | "bin" | "table" | "street" | "stairs" | "door" | "stand" | "stage" | "plant" | "rock" | "lighting" | "landmark";

export interface DecorCategory {
  id: DecorCategoryId;
  label: string;
  movable: boolean;
  defaultSpeedKmh: number;
}

export type DecorModelSize = { height: number } | { length: number } | { width: number } | { width: number; length: number };

export interface DecorModel {
  id: string;
  category: DecorCategoryId;
  label: string;
  /** Remplace `DecorCategory.movable` : un vélo sans cycliste ne roule pas seul. */
  movable?: boolean;
  /** Son animation a un sens qu'on peut inverser (`ShapeDecor.reverse`) : les marches d'un escalator. */
  reversible?: boolean;
  /** Une trémie vers le niveau inférieur : jamais au fond d'une zone creusée. */
  opening?: boolean;
  /** Sans ombre au sol : sous un objet ajouré, la tache ferait un aplat sombre ; un oiseau, un animal qui nage ou des galets n'en portent pas. */
  shadowless?: boolean;
  /**
   * Construit par le moteur à la largeur et à la longueur de l'élément (`ShapeDecor.width`/`length`,
   * en mètres) plutôt que chargé depuis `url` ; `size` en donne alors les dimensions par défaut.
   */
  procedural?: "stairs-opening" | "escalator-open" | "stairs-open" | "stairs-solid" | "stairs-solid-landing" | "stage" | "seating" | "picnic" | "tree-sakura" | "tree-pine-japanese" | "tree-olive" | "tree-olive-potted" | "lantern-garland" | "yatai" | "info-desk" | "first-aid-post" | "fence" | "rock-massif" | "castle" | "train-locomotive" | "train-tender" | "train-coach";
  /** Aménagement d'une scène (`procedural: "stage"`) : concert par défaut. */
  stageStyle?: "concert" | "dance" | "talk";
  /** Finition d'une clôture (`procedural: "fence"`), dont la largeur est la longueur. */
  fenceStyle?: "wood" | "mesh" | "glass";
  /** Silhouette d'un massif rocheux (`procedural: "rock-massif"`). */
  massifStyle?: "peak" | "plateau" | "wall";
  /**
   * Vitesse, en km/h à l'échelle 1, à laquelle le clip `walk` fait avancer les pieds au sol : l'animation
   * est jouée en proportion de la vitesse réelle pour que l'objet ne glisse pas. Absente : celle d'un piéton.
   */
  walkSpeedKmh?: number;
  /** Hauteur par défaut, en mètres, d'un modèle construit par le moteur réglable en hauteur (`ShapeDecor.height`). */
  defaultHeight?: number;
  /** Matériau d'un escalier plein (`procedural: "stairs-solid"`). */
  finish?: "wood" | "concrete";
  /** Dénivelé par défaut, en mètres, d'un escalier ou escalator posé à l'air libre. */
  rise?: number;
  /** Émet de la fumée depuis son nœud `SmokeSource`, d'intensité réglable (`ShapeDecor.smoke`). */
  smokes?: boolean;
  /** Noms des parties de couleur, par le mot qui finit le nom des morceaux du modèle (`HeadGrey` → `Grey`). */
  partNames?: Record<string, string>;
  /** Pelage dessiné par le moteur à la place de la peinture du modèle. */
  coat?: "zebra" | "giraffe" | "tiger";
  url: string;
  thumbnailUrl: string;
  size: DecorModelSize;
}

export const DECOR_CATEGORIES: DecorCategory[] = [
  { id: "vehicle", label: "Véhicules", movable: true, defaultSpeedKmh: 30 },
  { id: "person", label: "Personnes", movable: true, defaultSpeedKmh: 5 },
  { id: "animal", label: "Animaux", movable: true, defaultSpeedKmh: 5 },
  { id: "bench", label: "Bancs", movable: false, defaultSpeedKmh: 0 },
  { id: "bin", label: "Poubelles", movable: false, defaultSpeedKmh: 0 },
  { id: "table", label: "Tables & chaises", movable: false, defaultSpeedKmh: 0 },
  { id: "street", label: "Mobilier urbain", movable: false, defaultSpeedKmh: 0 },
  { id: "stairs", label: "Escaliers & escalators", movable: false, defaultSpeedKmh: 0 },
  { id: "door", label: "Portes", movable: false, defaultSpeedKmh: 0 },
  { id: "stand", label: "Stands", movable: false, defaultSpeedKmh: 0 },
  { id: "stage", label: "Scènes", movable: false, defaultSpeedKmh: 0 },
  { id: "plant", label: "Arbres & plantes", movable: false, defaultSpeedKmh: 0 },
  { id: "rock", label: "Rochers", movable: false, defaultSpeedKmh: 0 },
  { id: "lighting", label: "Éclairage", movable: false, defaultSpeedKmh: 0 },
  { id: "landmark", label: "Monuments", movable: false, defaultSpeedKmh: 0 },
];

export const DECOR_MODELS: DecorModel[] = [
  {
    id: "car-sedan",
    category: "vehicle",
    label: "Berline",
    url: new URL("../../assets/decor/vehicles/sedan.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-sedan.png", import.meta.url).href,
    size: { length: 4.6 },
  },
  {
    id: "car-hatchback",
    category: "vehicle",
    label: "Citadine",
    url: new URL("../../assets/decor/vehicles/hatchback.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-hatchback.png", import.meta.url).href,
    size: { length: 4.0 },
  },
  {
    id: "car-suv",
    category: "vehicle",
    label: "SUV",
    url: new URL("../../assets/decor/vehicles/suv.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-suv.png", import.meta.url).href,
    size: { length: 4.7 },
  },
  {
    id: "car-taxi",
    category: "vehicle",
    label: "Taxi",
    url: new URL("../../assets/decor/vehicles/taxi.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-taxi.png", import.meta.url).href,
    size: { length: 4.6 },
  },
  {
    id: "car-police",
    category: "vehicle",
    label: "Police nationale",
    url: new URL("../../assets/decor/vehicles/police.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-police.png", import.meta.url).href,
    size: { length: 4.8 },
  },
  {
    id: "car-gendarmerie",
    category: "vehicle",
    label: "Gendarmerie",
    url: new URL("../../assets/decor/vehicles/gendarmerie.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-gendarmerie.png", import.meta.url).href,
    size: { length: 4.8 },
  },
  {
    id: "car-van",
    category: "vehicle",
    label: "Utilitaire",
    url: new URL("../../assets/decor/vehicles/van.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-van.png", import.meta.url).href,
    size: { length: 5.0 },
  },
  {
    id: "car-postal-van",
    category: "vehicle",
    label: "Fourgon postal",
    url: new URL("../../assets/decor/vehicles/postal-van.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-postal-van.png", import.meta.url).href,
    size: { length: 5.0 },
  },
  {
    id: "car-delivery",
    category: "vehicle",
    label: "Livraison",
    url: new URL("../../assets/decor/vehicles/delivery.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-delivery.png", import.meta.url).href,
    size: { length: 6.8 },
  },
  {
    id: "car-bus",
    category: "vehicle",
    label: "Bus",
    url: new URL("../../assets/decor/vehicles/bus.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-bus.png", import.meta.url).href,
    size: { length: 12 },
  },
  {
    id: "car-pickup",
    category: "vehicle",
    label: "Pick-up",
    url: new URL("../../assets/decor/vehicles/pickup.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-pickup.png", import.meta.url).href,
    size: { length: 5.3 },
  },
  {
    id: "car-truck",
    category: "vehicle",
    label: "Camion",
    url: new URL("../../assets/decor/vehicles/truck.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-truck.png", import.meta.url).href,
    size: { length: 6 },
  },
  {
    id: "car-firetruck",
    category: "vehicle",
    label: "Pompiers",
    url: new URL("../../assets/decor/vehicles/firetruck.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-firetruck.png", import.meta.url).href,
    size: { length: 9 },
  },
  {
    id: "car-ambulance",
    category: "vehicle",
    label: "SAMU",
    url: new URL("../../assets/decor/vehicles/ambulance.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/car-ambulance.png", import.meta.url).href,
    size: { length: 6 },
  },
  {
    id: "train-locomotive",
    category: "vehicle",
    label: "Locomotive à vapeur",
    procedural: "train-locomotive",
    smokes: true,
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/train-locomotive.png", import.meta.url).href,
    size: { length: 10 },
  },
  {
    id: "train-tender",
    category: "vehicle",
    label: "Tender de locomotive",
    procedural: "train-tender",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/train-tender.png", import.meta.url).href,
    size: { length: 5 },
  },
  {
    id: "train-coach",
    category: "vehicle",
    label: "Voiture d'excursion",
    procedural: "train-coach",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/train-coach.png", import.meta.url).href,
    size: { length: 9 },
  },
  {
    id: "bike-red",
    category: "vehicle",
    movable: false,
    label: "Vélo",
    url: new URL("../../assets/decor/vehicles/bicycle-red.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/bike-red.png", import.meta.url).href,
    size: { length: 1.75 },
  },
  {
    id: "bike-blue",
    category: "vehicle",
    movable: false,
    label: "Vélo bleu",
    url: new URL("../../assets/decor/vehicles/bicycle-blue.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/bike-blue.png", import.meta.url).href,
    size: { length: 1.75 },
  },
  {
    id: "person-casual-female",
    category: "person",
    label: "Passante",
    url: new URL("../../assets/decor/people/casual-female.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-casual-female.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-casual-male",
    category: "person",
    label: "Passant",
    url: new URL("../../assets/decor/people/casual-male.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-casual-male.png", import.meta.url).href,
    size: { height: 1.79 },
  },
  {
    id: "person-casual2-female",
    category: "person",
    label: "Passante en jean",
    url: new URL("../../assets/decor/people/casual2-female.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-casual2-female.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-casual2-male",
    category: "person",
    label: "Passant en jean",
    url: new URL("../../assets/decor/people/casual2-male.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-casual2-male.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-casual3-female",
    category: "person",
    label: "Passante en débardeur",
    url: new URL("../../assets/decor/people/casual3-female.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-casual3-female.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-casual3-male",
    category: "person",
    label: "Passant en débardeur",
    url: new URL("../../assets/decor/people/casual3-male.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-casual3-male.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-casual-bald",
    category: "person",
    label: "Passant chauve",
    url: new URL("../../assets/decor/people/casual-bald.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-casual-bald.png", import.meta.url).href,
    size: { height: 1.69 },
  },
  {
    id: "person-hiker-bearded",
    category: "person",
    label: "Randonneur barbu",
    url: new URL("../../assets/decor/people/hiker-bearded.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-hiker-bearded.png", import.meta.url).href,
    size: { height: 1.69 },
  },
  {
    id: "person-smoker-male",
    category: "person",
    label: "Fumeur",
    smokes: true,
    url: new URL("../../assets/decor/people/smoker-male.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-smoker-male.png", import.meta.url).href,
    size: { height: 1.79 },
  },
  {
    id: "person-smoker-female",
    category: "person",
    label: "Fumeuse",
    smokes: true,
    url: new URL("../../assets/decor/people/smoker-female.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-smoker-female.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-suit-female",
    category: "person",
    label: "Femme en costume",
    url: new URL("../../assets/decor/people/suit-female.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-suit-female.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-suit-male",
    category: "person",
    label: "Homme en costume",
    url: new URL("../../assets/decor/people/suit-male.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-suit-male.png", import.meta.url).href,
    size: { height: 1.79 },
  },
  {
    id: "person-dress-red",
    category: "person",
    label: "Femme en robe rouge",
    url: new URL("../../assets/decor/people/dress-red.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-dress-red.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-dress-navy",
    category: "person",
    label: "Femme en robe bleue",
    url: new URL("../../assets/decor/people/dress-navy.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-dress-navy.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-dress-green",
    category: "person",
    label: "Femme en robe verte",
    url: new URL("../../assets/decor/people/dress-green.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-dress-green.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-dress-pink",
    category: "person",
    label: "Femme en robe rose",
    url: new URL("../../assets/decor/people/dress-pink.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-dress-pink.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-worker-female",
    category: "person",
    label: "Ouvrière",
    url: new URL("../../assets/decor/people/worker-female.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-worker-female.png", import.meta.url).href,
    size: { height: 1.84 },
  },
  {
    id: "person-worker-male",
    category: "person",
    label: "Ouvrier",
    url: new URL("../../assets/decor/people/worker-male.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-worker-male.png", import.meta.url).href,
    size: { height: 1.79 },
  },
  {
    id: "person-wheelchair",
    category: "person",
    label: "Personne en fauteuil",
    url: new URL("../../assets/decor/people/wheelchair-manual.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-wheelchair.png", import.meta.url).href,
    size: { height: 1.72 },
  },
  {
    id: "person-wheelchair-power",
    category: "person",
    label: "Fauteuil électrique",
    url: new URL("../../assets/decor/people/wheelchair-power.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/person-wheelchair-power.png", import.meta.url).href,
    size: { height: 1.66 },
  },
  {
    id: "animal-dog-grey",
    category: "animal",
    label: "Chien",
    url: new URL("../../assets/decor/animals/dog-grey.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-dog-grey.png", import.meta.url).href,
    size: { length: 1 },
  },
  {
    id: "animal-dog-brown",
    category: "animal",
    label: "Chien marron",
    url: new URL("../../assets/decor/animals/dog-brown.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-dog-brown.png", import.meta.url).href,
    size: { length: 1 },
  },
  {
    id: "animal-cat",
    category: "animal",
    label: "Chat",
    url: new URL("../../assets/decor/animals/cat.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-cat.png", import.meta.url).href,
    size: { length: 0.75 },
  },
  {
    id: "animal-chibi-dino",
    category: "animal",
    label: "Dino chibi géant",
    url: new URL("../../assets/decor/animals/chibi-dino.glb", import.meta.url).href,
    partNames: { Green: "Peau", Grey: "Sourire", White: "Yeux", Eye: "Pupilles", Dark: "Pics" },
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-chibi-dino.png", import.meta.url).href,
    size: { height: 2.8 },
  },
  {
    id: "animal-giraffe",
    category: "animal",
    label: "Girafe",
    url: new URL("../../assets/decor/animals/giraffe.glb", import.meta.url).href,
    coat: "giraffe",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-giraffe.png", import.meta.url).href,
    walkSpeedKmh: 9.8,
    size: { height: 5 },
  },
  {
    id: "animal-zebra",
    category: "animal",
    label: "Zèbre",
    url: new URL("../../assets/decor/animals/zebra.glb", import.meta.url).href,
    coat: "zebra",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-zebra.png", import.meta.url).href,
    walkSpeedKmh: 5.0,
    size: { length: 2.5 },
  },
  {
    id: "animal-guanaco",
    category: "animal",
    label: "Guanaco",
    url: new URL("../../assets/decor/animals/guanaco.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-guanaco.png", import.meta.url).href,
    walkSpeedKmh: 2.5,
    size: { height: 1.8 },
  },
  {
    id: "animal-wolf",
    category: "animal",
    label: "Loup",
    url: new URL("../../assets/decor/animals/wolf.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-wolf.png", import.meta.url).href,
    walkSpeedKmh: 1.9,
    size: { length: 1.7 },
  },
  {
    id: "animal-lemur",
    category: "animal",
    label: "Lémurien catta",
    url: new URL("../../assets/decor/animals/lemur.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-lemur.png", import.meta.url).href,
    walkSpeedKmh: 1.1,
    size: { height: 0.75 },
  },
  {
    id: "animal-jaguar",
    category: "animal",
    label: "Jaguar",
    url: new URL("../../assets/decor/animals/jaguar.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-jaguar.png", import.meta.url).href,
    walkSpeedKmh: 4.8,
    size: { length: 2.3 },
  },
  {
    id: "animal-lynx",
    category: "animal",
    label: "Lynx",
    url: new URL("../../assets/decor/animals/lynx.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-lynx.png", import.meta.url).href,
    walkSpeedKmh: 2.8,
    size: { length: 1.1 },
  },
  {
    id: "animal-kudu",
    category: "animal",
    label: "Grand koudou",
    url: new URL("../../assets/decor/animals/kudu.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-kudu.png", import.meta.url).href,
    walkSpeedKmh: 4.2,
    size: { height: 2.6 },
  },
  {
    id: "animal-gazelle",
    category: "animal",
    label: "Gazelle",
    url: new URL("../../assets/decor/animals/gazelle.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-gazelle.png", import.meta.url).href,
    walkSpeedKmh: 3.0,
    size: { height: 1.4 },
  },
  {
    id: "animal-addax",
    category: "animal",
    label: "Addax",
    url: new URL("../../assets/decor/animals/addax.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-addax.png", import.meta.url).href,
    walkSpeedKmh: 2.9,
    size: { height: 1.7 },
  },
  {
    id: "animal-roan-antelope",
    category: "animal",
    label: "Antilope rouanne",
    url: new URL("../../assets/decor/animals/roan-antelope.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-roan-antelope.png", import.meta.url).href,
    walkSpeedKmh: 4.4,
    size: { height: 2.1 },
  },
  {
    id: "animal-tapir",
    category: "animal",
    label: "Tapir",
    url: new URL("../../assets/decor/animals/tapir.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-tapir.png", import.meta.url).href,
    walkSpeedKmh: 3.1,
    size: { length: 2 },
  },
  {
    id: "animal-fossa",
    category: "animal",
    label: "Fossa",
    url: new URL("../../assets/decor/animals/fossa.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-fossa.png", import.meta.url).href,
    walkSpeedKmh: 2.6,
    size: { length: 1.4 },
  },
  {
    id: "animal-bush-dog",
    category: "animal",
    label: "Chien des buissons",
    url: new URL("../../assets/decor/animals/bush-dog.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-bush-dog.png", import.meta.url).href,
    walkSpeedKmh: 2.2,
    size: { length: 0.9 },
  },
  {
    id: "animal-wolverine",
    category: "animal",
    label: "Glouton",
    url: new URL("../../assets/decor/animals/wolverine.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-wolverine.png", import.meta.url).href,
    walkSpeedKmh: 3.0,
    size: { length: 1.1 },
  },
  {
    id: "animal-peccary",
    category: "animal",
    label: "Pécari",
    url: new URL("../../assets/decor/animals/peccary.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-peccary.png", import.meta.url).href,
    walkSpeedKmh: 2.8,
    size: { length: 1 },
  },
  {
    id: "animal-mara",
    category: "animal",
    label: "Mara",
    url: new URL("../../assets/decor/animals/mara.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-mara.png", import.meta.url).href,
    walkSpeedKmh: 2.2,
    size: { length: 0.8 },
  },
  {
    id: "animal-flamingo",
    category: "animal",
    label: "Flamant rose",
    shadowless: true,
    url: new URL("../../assets/decor/animals/flamingo.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-flamingo.png", import.meta.url).href,
    walkSpeedKmh: 2.4,
    size: { height: 1.3 },
  },
  {
    id: "animal-ostrich",
    category: "animal",
    label: "Autruche",
    shadowless: true,
    url: new URL("../../assets/decor/animals/ostrich.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-ostrich.png", import.meta.url).href,
    walkSpeedKmh: 6.9,
    size: { height: 2.5 },
  },
  {
    id: "animal-rhea",
    category: "animal",
    label: "Nandou",
    shadowless: true,
    url: new URL("../../assets/decor/animals/rhea.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-rhea.png", import.meta.url).href,
    walkSpeedKmh: 2.5,
    size: { height: 1.5 },
  },
  {
    id: "animal-crowned-crane",
    category: "animal",
    label: "Grue couronnée",
    shadowless: true,
    url: new URL("../../assets/decor/animals/crowned-crane.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-crowned-crane.png", import.meta.url).href,
    walkSpeedKmh: 1.9,
    size: { height: 1 },
  },
  {
    id: "animal-ground-hornbill",
    category: "animal",
    label: "Calao terrestre",
    shadowless: true,
    url: new URL("../../assets/decor/animals/ground-hornbill.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-ground-hornbill.png", import.meta.url).href,
    walkSpeedKmh: 1.3,
    size: { height: 1 },
  },
  {
    id: "animal-penguin",
    category: "animal",
    label: "Manchot",
    shadowless: true,
    url: new URL("../../assets/decor/animals/penguin.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-penguin.png", import.meta.url).href,
    walkSpeedKmh: 1.2,
    size: { height: 0.65 },
  },
  {
    id: "animal-penguin-swimming",
    category: "animal",
    label: "Manchot qui nage",
    shadowless: true,
    url: new URL("../../assets/decor/animals/penguin-swimming.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-penguin-swimming.png", import.meta.url).href,
    walkSpeedKmh: 1.5,
    size: { length: 0.65 },
  },
  {
    id: "animal-sea-lion",
    category: "animal",
    label: "Otarie",
    url: new URL("../../assets/decor/animals/sea-lion.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-sea-lion.png", import.meta.url).href,
    walkSpeedKmh: 1.5,
    size: { length: 2.2 },
  },
  {
    id: "animal-sea-lion-swimming",
    category: "animal",
    label: "Otarie qui nage",
    shadowless: true,
    url: new URL("../../assets/decor/animals/sea-lion-swimming.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-sea-lion-swimming.png", import.meta.url).href,
    walkSpeedKmh: 4,
    size: { length: 2.2 },
  },
  {
    id: "animal-manatee",
    category: "animal",
    label: "Lamantin",
    shadowless: true,
    url: new URL("../../assets/decor/animals/manatee.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-manatee.png", import.meta.url).href,
    walkSpeedKmh: 2,
    size: { length: 3 },
  },
  {
    id: "animal-tortoise",
    category: "animal",
    label: "Tortue rayonnée",
    url: new URL("../../assets/decor/animals/tortoise.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-tortoise.png", import.meta.url).href,
    walkSpeedKmh: 0.5,
    size: { length: 0.4 },
  },
  {
    id: "animal-vulture",
    category: "animal",
    label: "Vautour en vol",
    shadowless: true,
    url: new URL("../../assets/decor/animals/vulture.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-vulture.png", import.meta.url).href,
    walkSpeedKmh: 16,
    size: { width: 2.6 },
  },
  {
    id: "animal-macaw",
    category: "animal",
    label: "Ara en vol",
    shadowless: true,
    url: new URL("../../assets/decor/animals/macaw.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-macaw.png", import.meta.url).href,
    walkSpeedKmh: 18,
    size: { width: 1.1 },
  },
  {
    id: "animal-lion",
    category: "animal",
    label: "Lion",
    url: new URL("../../assets/decor/animals/lion.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-lion.png", import.meta.url).href,
    walkSpeedKmh: 2.9,
    size: { length: 2.6 },
  },
  {
    id: "animal-tiger",
    category: "animal",
    label: "Tigre",
    url: new URL("../../assets/decor/animals/tiger.glb", import.meta.url).href,
    coat: "tiger",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-tiger.png", import.meta.url).href,
    walkSpeedKmh: 2.9,
    size: { length: 2.9 },
  },
  {
    id: "animal-puma",
    category: "animal",
    label: "Puma",
    url: new URL("../../assets/decor/animals/puma.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-puma.png", import.meta.url).href,
    walkSpeedKmh: 2.8,
    size: { length: 2.1 },
  },
  {
    id: "animal-rhino",
    category: "animal",
    label: "Rhinocéros blanc",
    url: new URL("../../assets/decor/animals/rhino.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-rhino.png", import.meta.url).href,
    walkSpeedKmh: 3.4,
    size: { length: 4 },
  },
  {
    id: "animal-elephant",
    category: "animal",
    label: "Éléphant",
    url: new URL("../../assets/decor/animals/elephant.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-elephant.png", import.meta.url).href,
    walkSpeedKmh: 4,
    size: { height: 3.5 },
  },
  {
    id: "animal-pudu",
    category: "animal",
    label: "Pudu",
    url: new URL("../../assets/decor/animals/pudu.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-pudu.png", import.meta.url).href,
    walkSpeedKmh: 1.3,
    size: { height: 0.45 },
  },
  {
    id: "animal-baboon",
    category: "animal",
    label: "Babouin",
    url: new URL("../../assets/decor/animals/baboon.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-baboon.png", import.meta.url).href,
    walkSpeedKmh: 2.6,
    size: { length: 1.3 },
  },
  {
    id: "animal-otter",
    category: "animal",
    label: "Loutre",
    url: new URL("../../assets/decor/animals/otter.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-otter.png", import.meta.url).href,
    walkSpeedKmh: 2.2,
    size: { length: 1.1 },
  },
  {
    id: "animal-meerkat",
    category: "animal",
    label: "Suricate",
    url: new URL("../../assets/decor/animals/meerkat.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-meerkat.png", import.meta.url).href,
    walkSpeedKmh: 0.5,
    size: { height: 0.32 },
  },
  {
    id: "animal-woolly-monkey",
    category: "animal",
    label: "Singe laineux",
    url: new URL("../../assets/decor/animals/woolly-monkey.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-woolly-monkey.png", import.meta.url).href,
    walkSpeedKmh: 0.5,
    size: { height: 0.55 },
  },
  {
    id: "animal-marmoset",
    category: "animal",
    label: "Ouistiti",
    url: new URL("../../assets/decor/animals/marmoset.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-marmoset.png", import.meta.url).href,
    walkSpeedKmh: 0.5,
    size: { height: 0.2 },
  },
  {
    id: "animal-sifaka",
    category: "animal",
    label: "Propithèque",
    url: new URL("../../assets/decor/animals/sifaka.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-sifaka.png", import.meta.url).href,
    walkSpeedKmh: 0.5,
    size: { height: 0.55 },
  },
  {
    id: "animal-fruit-bat",
    category: "animal",
    label: "Roussette en vol",
    url: new URL("../../assets/decor/animals/fruit-bat.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/animal-fruit-bat.png", import.meta.url).href,
    walkSpeedKmh: 18,
    size: { width: 0.9 },
  },
  {
    id: "bench-park",
    category: "bench",
    label: "Banc de parc",
    url: new URL("../../assets/decor/furniture/bench-park.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/bench-park.png", import.meta.url).href,
    size: { width: 1.8 },
  },
  {
    id: "bench-wood",
    category: "bench",
    label: "Banc bois",
    url: new URL("../../assets/decor/furniture/bench-wood.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/bench-wood.png", import.meta.url).href,
    size: { width: 1.8 },
  },
  {
    id: "bench-metal",
    category: "bench",
    label: "Banc métal",
    url: new URL("../../assets/decor/furniture/bench-metal.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/bench-metal.png", import.meta.url).href,
    size: { width: 1.8 },
  },
  {
    id: "bench-plain",
    category: "bench",
    label: "Banquette",
    url: new URL("../../assets/decor/furniture/bench-plain.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/bench-plain.png", import.meta.url).href,
    size: { width: 1.8 },
  },
  {
    id: "bin-round",
    category: "bin",
    label: "Corbeille",
    url: new URL("../../assets/decor/furniture/bin-round.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/bin-round.png", import.meta.url).href,
    size: { height: 1.0 },
  },
  {
    id: "bin-container",
    category: "bin",
    label: "Conteneur",
    url: new URL("../../assets/decor/furniture/bin-container.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/bin-container.png", import.meta.url).href,
    size: { height: 1.33 },
  },
  {
    id: "bin-sorting",
    category: "bin",
    label: "Tri sélectif",
    url: new URL("../../assets/decor/furniture/bin-sorting.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/bin-sorting.png", import.meta.url).href,
    size: { height: 1.06 },
  },
  {
    id: "table-bistro",
    category: "table",
    label: "Table bistrot",
    url: new URL("../../assets/decor/furniture/table-bistro.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/table-bistro.png", import.meta.url).href,
    size: { height: 0.75 },
  },
  {
    id: "table-square",
    category: "table",
    label: "Table carrée",
    url: new URL("../../assets/decor/furniture/table-square.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/table-square.png", import.meta.url).href,
    size: { height: 0.75 },
  },
  {
    id: "table-dining",
    category: "table",
    label: "Grande table",
    url: new URL("../../assets/decor/furniture/table-dining.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/table-dining.png", import.meta.url).href,
    size: { height: 0.76 },
  },
  {
    id: "table-picnic",
    category: "table",
    label: "Table de pique-nique",
    url: new URL("../../assets/decor/furniture/table-picnic.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/table-picnic.png", import.meta.url).href,
    size: { height: 0.78 },
  },
  {
    id: "table-picnic-area",
    category: "table",
    label: "Tables de pique-nique en rangées",
    procedural: "picnic",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/table-picnic-area.png", import.meta.url).href,
    size: { width: 9, length: 7 },
  },
  {
    id: "table-high",
    category: "table",
    label: "Mange-debout",
    url: new URL("../../assets/decor/furniture/table-high.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/table-high.png", import.meta.url).href,
    size: { height: 1.1 },
  },
  {
    id: "table-folding",
    category: "table",
    label: "Table pliante",
    url: new URL("../../assets/decor/furniture/table-folding.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/table-folding.png", import.meta.url).href,
    size: { height: 0.74 },
  },
  {
    id: "table-terrace-set",
    category: "table",
    label: "Table de terrasse et chaises",
    url: new URL("../../assets/decor/furniture/table-terrace-set.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/table-terrace-set.png", import.meta.url).href,
    size: { height: 0.87 },
  },
  {
    id: "chair-bistro",
    category: "table",
    label: "Chaise bistrot",
    url: new URL("../../assets/decor/furniture/chair-bistro.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/chair-bistro.png", import.meta.url).href,
    size: { height: 0.87 },
  },
  {
    id: "chair-wood",
    category: "table",
    label: "Chaise en bois",
    url: new URL("../../assets/decor/furniture/chair-wood.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/chair-wood.png", import.meta.url).href,
    size: { height: 0.96 },
  },
  {
    id: "chair-plastic",
    category: "table",
    label: "Chaise de jardin",
    url: new URL("../../assets/decor/furniture/chair-plastic.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/chair-plastic.png", import.meta.url).href,
    size: { height: 0.9 },
  },
  {
    id: "chair-folding",
    category: "table",
    label: "Chaise pliante",
    url: new URL("../../assets/decor/furniture/chair-folding.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/chair-folding.png", import.meta.url).href,
    size: { height: 0.96 },
  },
  {
    id: "chair-stool",
    category: "table",
    label: "Tabouret de bar",
    url: new URL("../../assets/decor/furniture/chair-stool.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/chair-stool.png", import.meta.url).href,
    size: { height: 0.8 },
  },
  {
    id: "street-cone",
    category: "street",
    label: "Cône de chantier",
    url: new URL("../../assets/decor/street/cone.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-cone.png", import.meta.url).href,
    size: { height: 0.5 },
  },
  {
    id: "street-barrier",
    category: "street",
    label: "Barrière de travaux",
    url: new URL("../../assets/decor/street/barrier.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-barrier.png", import.meta.url).href,
    size: { height: 1 },
  },
  {
    id: "street-crowd-barrier",
    category: "street",
    label: "Barrière Vauban",
    shadowless: true,
    url: new URL("../../assets/decor/street/crowd-barrier.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-crowd-barrier.png", import.meta.url).href,
    size: { height: 1.12 },
  },
  {
    id: "street-construction-barrier",
    category: "street",
    label: "Séparateur béton",
    url: new URL("../../assets/decor/street/construction-barrier.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-construction-barrier.png", import.meta.url).href,
    size: { height: 0.8 },
  },
  {
    id: "street-construction-fence",
    category: "street",
    label: "Barrière de ville",
    shadowless: true,
    url: new URL("../../assets/decor/street/construction-fence.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-construction-fence.png", import.meta.url).href,
    size: { height: 1.1 },
  },
  {
    id: "street-parasol",
    category: "street",
    label: "Table et parasol",
    url: new URL("../../assets/decor/street/parasol.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-parasol.png", import.meta.url).href,
    size: { height: 2.6 },
  },
  {
    id: "street-planter",
    category: "street",
    label: "Jardinière",
    url: new URL("../../assets/decor/street/planter.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-planter.png", import.meta.url).href,
    size: { height: 0.6 },
  },
  {
    id: "fence-wood",
    category: "street",
    label: "Clôture en bois",
    procedural: "fence",
    fenceStyle: "wood",
    shadowless: true,
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/fence-wood.png", import.meta.url).href,
    size: { width: 4, length: 0.2 },
  },
  {
    id: "fence-mesh",
    category: "street",
    label: "Grillage",
    procedural: "fence",
    fenceStyle: "mesh",
    shadowless: true,
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/fence-mesh.png", import.meta.url).href,
    size: { width: 4, length: 0.2 },
  },
  {
    id: "fence-glass",
    category: "street",
    label: "Paroi vitrée",
    procedural: "fence",
    fenceStyle: "glass",
    shadowless: true,
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/fence-glass.png", import.meta.url).href,
    size: { width: 4, length: 0.2 },
  },
  {
    id: "street-climbing-frame",
    category: "street",
    label: "Structure d'escalade",
    url: new URL("../../assets/decor/play/climbing-frame.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-climbing-frame.png", import.meta.url).href,
    size: { height: 6.2 },
  },
  {
    id: "street-entrance-gate",
    category: "street",
    label: "Portique d'entrée",
    url: new URL("../../assets/decor/street/entrance-gate.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-entrance-gate.png", import.meta.url).href,
    size: { width: 10.4 },
  },
  {
    id: "street-traffic-light",
    category: "street",
    label: "Feu tricolore",
    url: new URL("../../assets/decor/street/traffic-light.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-traffic-light.png", import.meta.url).href,
    size: { height: 3.2 },
  },
  {
    id: "street-torii",
    category: "street",
    label: "Torii",
    url: new URL("../../assets/decor/street/torii.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/street-torii.png", import.meta.url).href,
    size: { height: 4.7 },
  },
  {
    id: "stairs-escalator",
    category: "stairs",
    label: "Escalator",
    reversible: true,
    url: new URL("../../assets/decor/stairs/escalator.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-escalator.png", import.meta.url).href,
    size: { length: 7.6 },
  },
  {
    id: "stairs-escalator-down",
    category: "stairs",
    label: "Escalator vers le niveau inférieur",
    reversible: true,
    opening: true,
    url: new URL("../../assets/decor/stairs/escalator-down.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-escalator-down.png", import.meta.url).href,
    size: { length: 7.7 },
  },
  {
    id: "stairs-concrete-down",
    category: "stairs",
    label: "Escalier vers le niveau inférieur",
    opening: true,
    url: new URL("../../assets/decor/stairs/stairs-concrete-down.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-concrete-down.png", import.meta.url).href,
    size: { length: 7.7 },
  },
  {
    id: "stairs-wide-down",
    category: "stairs",
    label: "Grand escalier vers le niveau inférieur",
    opening: true,
    procedural: "stairs-opening",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-wide-down.png", import.meta.url).href,
    size: { width: 12, length: 13 },
  },
  {
    id: "stairs-escalator-open",
    category: "stairs",
    label: "Escalator en plein air",
    reversible: true,
    procedural: "escalator-open",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-escalator.png", import.meta.url).href,
    size: { width: 1.6 },
  },
  {
    id: "stairs-open",
    category: "stairs",
    label: "Escalier en plein air",
    procedural: "stairs-open",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-landing-concrete.png", import.meta.url).href,
    size: { width: 3 },
  },
  {
    id: "stairs-straight",
    category: "stairs",
    label: "Escalier bois",
    procedural: "stairs-solid",
    finish: "wood",
    rise: 3,
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-straight.png", import.meta.url).href,
    size: { width: 1.8 },
  },
  {
    id: "stairs-landing",
    category: "stairs",
    label: "Escalier bois avec palier",
    procedural: "stairs-solid-landing",
    finish: "wood",
    rise: 3,
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-landing.png", import.meta.url).href,
    size: { width: 1.8 },
  },
  {
    id: "stairs-straight-concrete",
    category: "stairs",
    label: "Escalier béton",
    procedural: "stairs-solid",
    finish: "concrete",
    rise: 3,
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-straight-concrete.png", import.meta.url).href,
    size: { width: 1.8 },
  },
  {
    id: "stairs-landing-concrete",
    category: "stairs",
    label: "Escalier béton avec palier",
    procedural: "stairs-solid-landing",
    finish: "concrete",
    rise: 3,
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stairs-landing-concrete.png", import.meta.url).href,
    size: { width: 1.8 },
  },
  {
    id: "door-solid",
    category: "door",
    label: "Porte pleine",
    url: new URL("../../assets/decor/doors/door-solid.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/door-solid.png", import.meta.url).href,
    size: { height: 2.18 },
  },
  {
    id: "door-glass",
    category: "door",
    label: "Porte vitrée",
    url: new URL("../../assets/decor/doors/door-glass.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/door-glass.png", import.meta.url).href,
    size: { height: 2.21 },
  },
  {
    id: "door-double-glass",
    category: "door",
    label: "Double porte vitrée",
    url: new URL("../../assets/decor/doors/door-double-glass.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/door-double-glass.png", import.meta.url).href,
    size: { height: 2.26 },
  },
  {
    id: "door-sliding",
    category: "door",
    label: "Porte coulissante automatique",
    url: new URL("../../assets/decor/doors/door-sliding.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/door-sliding.png", import.meta.url).href,
    size: { height: 2.6 },
  },
  {
    id: "door-revolving",
    category: "door",
    label: "Porte tambour",
    url: new URL("../../assets/decor/doors/door-revolving.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/door-revolving.png", import.meta.url).href,
    size: { height: 2.7 },
  },
  {
    id: "door-emergency",
    category: "door",
    label: "Porte de secours",
    url: new URL("../../assets/decor/doors/door-emergency.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/door-emergency.png", import.meta.url).href,
    size: { height: 2.45 },
  },
  {
    id: "stand-booth",
    category: "stand",
    label: "Stand de salon",
    url: new URL("../../assets/decor/stands/stand-booth.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-booth.png", import.meta.url).href,
    size: { height: 2.8 },
  },
  {
    id: "stand-corner",
    category: "stand",
    label: "Stand d'angle",
    url: new URL("../../assets/decor/stands/stand-corner.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-corner.png", import.meta.url).href,
    size: { height: 2.8 },
  },
  {
    id: "stand-island",
    category: "stand",
    label: "Stand îlot",
    url: new URL("../../assets/decor/stands/stand-island.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-island.png", import.meta.url).href,
    size: { height: 4.1 },
  },
  {
    id: "stand-market",
    category: "stand",
    label: "Étal de marché",
    url: new URL("../../assets/decor/stands/stand-market.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-market.png", import.meta.url).href,
    size: { height: 2.59 },
  },
  {
    id: "stand-tent",
    category: "stand",
    label: "Barnum",
    url: new URL("../../assets/decor/stands/stand-tent.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-tent.png", import.meta.url).href,
    size: { height: 2.9 },
  },
  {
    id: "stand-popup",
    category: "stand",
    label: "Kiosque boutique",
    url: new URL("../../assets/decor/stands/stand-popup.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-popup.png", import.meta.url).href,
    size: { height: 2.76 },
  },
  {
    id: "stand-kiosk",
    category: "stand",
    label: "Kiosque de restauration",
    url: new URL("../../assets/decor/stands/stand-kiosk.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-kiosk.png", import.meta.url).href,
    size: { height: 2.92 },
  },
  {
    id: "stand-yatai",
    category: "stand",
    label: "Yatai (échoppe de rue)",
    procedural: "yatai",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-yatai.png", import.meta.url).href,
    size: { height: 2.9 },
  },
  {
    id: "stand-info",
    category: "stand",
    label: "Comptoir d'accueil",
    procedural: "info-desk",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-info.png", import.meta.url).href,
    size: { height: 3.15 },
  },
  {
    id: "stand-first-aid",
    category: "stand",
    label: "Poste de secours",
    procedural: "first-aid-post",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stand-first-aid.png", import.meta.url).href,
    size: { height: 3.1 },
  },
  {
    id: "landmark-castle",
    category: "landmark",
    label: "Château de conte de fées",
    procedural: "castle",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/landmark-castle.png", import.meta.url).href,
    size: { height: 50 },
  },
  {
    id: "tree-oak",
    category: "plant",
    label: "Chêne",
    url: new URL("../../assets/decor/plants/tree_oak.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/tree-oak.png", import.meta.url).href,
    size: { height: 9 },
  },
  {
    id: "tree-leafy",
    category: "plant",
    label: "Feuillu",
    url: new URL("../../assets/decor/plants/tree_default.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/tree-leafy.png", import.meta.url).href,
    size: { height: 7 },
  },
  {
    id: "tree-maple",
    category: "plant",
    label: "Érable",
    url: new URL("../../assets/decor/plants/tree_detailed_fall.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/tree-maple.png", import.meta.url).href,
    size: { height: 8 },
  },
  {
    id: "tree-sakura",
    category: "plant",
    label: "Cerisier en fleurs (sakura)",
    procedural: "tree-sakura",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/tree-sakura.png", import.meta.url).href,
    size: { height: 4.6 },
  },
  {
    id: "tree-pine-japanese",
    category: "plant",
    label: "Pin japonais (matsu)",
    procedural: "tree-pine-japanese",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/tree-pine-japanese.png", import.meta.url).href,
    size: { height: 4.2 },
  },
  {
    id: "tree-olive",
    category: "plant",
    label: "Olivier",
    procedural: "tree-olive",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/tree-olive.png", import.meta.url).href,
    size: { height: 4.5 },
  },
  {
    id: "tree-olive-potted",
    category: "plant",
    label: "Olivier en pot",
    procedural: "tree-olive-potted",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/tree-olive-potted.png", import.meta.url).href,
    size: { height: 2.2 },
  },
  {
    id: "tree-pine",
    category: "plant",
    label: "Pin",
    url: new URL("../../assets/decor/plants/tree_pineRoundA.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/tree-pine.png", import.meta.url).href,
    size: { height: 11 },
  },
  {
    id: "tree-palm",
    category: "plant",
    label: "Palmier",
    url: new URL("../../assets/decor/plants/tree_palmTall.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/tree-palm.png", import.meta.url).href,
    size: { height: 10 },
  },
  {
    id: "plant-bush",
    category: "plant",
    label: "Buisson",
    url: new URL("../../assets/decor/plants/plant_bushLarge.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/plant-bush.png", import.meta.url).href,
    size: { height: 1.3 },
  },
  {
    id: "plant-planter-shrub",
    category: "plant",
    label: "Arbuste en bac",
    url: new URL("../../assets/decor/plants/planter-shrub.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/plant-planter-shrub.png", import.meta.url).href,
    size: { height: 1.5 },
  },
  {
    id: "plant-pot",
    category: "plant",
    label: "Plante en pot",
    url: new URL("../../assets/decor/plants/potted-plant.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/plant-pot.png", import.meta.url).href,
    size: { height: 1.2 },
  },
  {
    id: "rock-small",
    category: "rock",
    label: "Petit rocher",
    url: new URL("../../assets/decor/rocks/rock-small.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-small.png", import.meta.url).href,
    size: { height: 0.31 },
  },
  {
    id: "rock-medium",
    category: "rock",
    label: "Rocher",
    url: new URL("../../assets/decor/rocks/rock-medium.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-medium.png", import.meta.url).href,
    size: { height: 0.73 },
  },
  {
    id: "rock-large",
    category: "rock",
    label: "Gros rocher",
    url: new URL("../../assets/decor/rocks/rock-large.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-large.png", import.meta.url).href,
    size: { height: 1.63 },
  },
  {
    id: "rock-sandstone",
    category: "rock",
    label: "Rocher de grès",
    url: new URL("../../assets/decor/rocks/rock-sandstone.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-sandstone.png", import.meta.url).href,
    size: { height: 1.17 },
  },
  {
    id: "rock-mossy",
    category: "rock",
    label: "Rocher moussu",
    url: new URL("../../assets/decor/rocks/rock-mossy.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-mossy.png", import.meta.url).href,
    size: { height: 1.1 },
  },
  {
    id: "rock-slab",
    category: "rock",
    label: "Dalle rocheuse",
    url: new URL("../../assets/decor/rocks/rock-slab.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-slab.png", import.meta.url).href,
    size: { height: 0.5 },
  },
  {
    id: "rock-standing",
    category: "rock",
    label: "Menhir",
    url: new URL("../../assets/decor/rocks/rock-standing.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-standing.png", import.meta.url).href,
    size: { height: 2.36 },
  },
  {
    id: "rock-cluster",
    category: "rock",
    label: "Groupe de rochers",
    url: new URL("../../assets/decor/rocks/rock-cluster.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-cluster.png", import.meta.url).href,
    size: { height: 0.84 },
  },
  {
    id: "rock-pebbles",
    category: "rock",
    label: "Galets",
    shadowless: true,
    url: new URL("../../assets/decor/rocks/rock-pebbles.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-pebbles.png", import.meta.url).href,
    size: { height: 0.13 },
  },
  {
    id: "rock-massif-peak",
    category: "rock",
    label: "Massif rocheux en pic",
    procedural: "rock-massif",
    massifStyle: "peak",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-massif-peak.png", import.meta.url).href,
    size: { width: 30, length: 30 },
    defaultHeight: 25,
  },
  {
    id: "rock-massif-plateau",
    category: "rock",
    label: "Plateau rocheux",
    procedural: "rock-massif",
    massifStyle: "plateau",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-massif-plateau.png", import.meta.url).href,
    size: { width: 20, length: 20 },
    defaultHeight: 8,
  },
  {
    id: "rock-massif-wall",
    category: "rock",
    label: "Muraille rocheuse",
    procedural: "rock-massif",
    massifStyle: "wall",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/rock-massif-wall.png", import.meta.url).href,
    size: { width: 4, length: 20 },
    defaultHeight: 5,
  },
  {
    id: "light-square",
    category: "lighting",
    label: "Lampadaire",
    url: new URL("../../assets/decor/lighting/light-square.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/light-square.png", import.meta.url).href,
    size: { height: 6 },
  },
  {
    id: "light-curved",
    category: "lighting",
    label: "Lampadaire à crosse",
    url: new URL("../../assets/decor/lighting/light-curved.glb", import.meta.url).href,
    thumbnailUrl: new URL("../../assets/decor/thumbnails/light-curved.png", import.meta.url).href,
    size: { height: 6.5 },
  },
  {
    id: "light-lantern-garland",
    category: "lighting",
    label: "Guirlande de lanternes (chōchin)",
    procedural: "lantern-garland",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/light-lantern-garland.png", import.meta.url).href,
    size: { height: 3.2 },
  },
  {
    id: "stage",
    category: "stage",
    label: "Scène de concert",
    procedural: "stage",
    stageStyle: "concert",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stage.png", import.meta.url).href,
    size: { width: 16, length: 8 },
  },
  {
    id: "stage-dance",
    category: "stage",
    label: "Scène de danse",
    procedural: "stage",
    stageStyle: "dance",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stage-dance.png", import.meta.url).href,
    size: { width: 16, length: 10 },
  },
  {
    id: "stage-talk",
    category: "stage",
    label: "Scène de conférence",
    procedural: "stage",
    stageStyle: "talk",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stage-talk.png", import.meta.url).href,
    size: { width: 12, length: 6 },
  },
  {
    id: "stage-seating",
    category: "stage",
    label: "Rangées de chaises",
    procedural: "seating",
    url: "",
    thumbnailUrl: new URL("../../assets/decor/thumbnails/stage-seating.png", import.meta.url).href,
    size: { width: 12, length: 8 },
  },
];

export const DECOR_MODELS_BY_ID: Record<string, DecorModel> = Object.fromEntries(DECOR_MODELS.map((model) => [model.id, model]));

/** Construit par le moteur à une taille fixe, qu'on règle à l'échelle comme un modèle chargé. */
export function isFixedProceduralModel(model: DecorModel): boolean {
  return model.procedural === "tree-sakura"
    || model.procedural === "tree-pine-japanese"
    || model.procedural === "tree-olive"
    || model.procedural === "tree-olive-potted"
    || model.procedural === "lantern-garland"
    || model.procedural === "yatai"
    || model.procedural === "info-desk"
    || model.procedural === "first-aid-post"
    || model.procedural === "castle"
    || model.procedural === "train-locomotive"
    || model.procedural === "train-tender"
    || model.procedural === "train-coach";
}

export function isDecorModelMovable(model: DecorModel): boolean {
  return model.movable ?? DECOR_CATEGORIES.find((category) => category.id === model.category)?.movable ?? false;
}

export const DECOR_CATEGORIES_BY_ID = Object.fromEntries(DECOR_CATEGORIES.map((category) => [category.id, category])) as Record<
  DecorCategoryId,
  DecorCategory
>;
